import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="bg-[#0A1128] text-white sticky top-0 z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_drive-hub-27/artifacts/1glibbg5_logo%202.png" 
              alt="Motoring Daily" 
              className="h-12 sm:h-14 w-auto transition-transform duration-300 hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link
              to="/news"
              className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              data-testid="nav-news"
            >
              News
            </Link>
            <Link
              to="/reviews"
              className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              data-testid="nav-reviews"
            >
              Reviews
            </Link>
            <Link
              to="/contact"
              className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              data-testid="nav-contact"
            >
              Contact
            </Link>
            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
                data-testid="nav-admin"
              >
                Motoringdaily
              </Link>
            )}
          </nav>

          {/* Right Icons */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="hover:text-[#00A859] transition-colors"
              data-testid="search-toggle-btn"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            {user ? (
              <div className="hidden md:flex items-center space-x-4">
                <span className="text-sm flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  {user.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-4 rounded-none transition-colors duration-200"
                  data-testid="logout-btn"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:flex items-center bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-4 rounded-none transition-colors duration-200"
                data-testid="signin-btn"
              >
                <User className="w-4 h-4 mr-1" />
                Motoringdaily
              </Link>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden hover:text-[#00A859] transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="py-4 border-t border-white/10">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles, reviews..."
                className="flex-1 bg-white/10 border border-white/20 focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859] rounded-none px-4 py-2 text-white placeholder-white/50 transition-colors"
                data-testid="search-input"
                autoFocus
              />
              <button
                type="submit"
                className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider px-6 py-2 rounded-none transition-colors duration-200"
                data-testid="search-submit-btn"
              >
                Search
              </button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              >
                Home
              </Link>
              <Link
                to="/news"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              >
                News
              </Link>
              <Link
                to="/reviews"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              >
                Reviews
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
              >
                Contact
              </Link>
              {user && user.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-semibold tracking-wide uppercase hover:text-[#00A859] transition-colors"
                >
                  Motoringdaily
                </Link>
              )}
              {user ? (
                <>
                  <div className="text-sm pt-2 border-t border-white/10">
                    {user.name}
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-4 rounded-none transition-colors duration-200 text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-4 rounded-none transition-colors duration-200 text-center"
                >
                  Motoringdaily Sign In
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};